import { useGLTF } from '@react-three/drei';
import { type FC, useCallback, useContext, useEffect, useState } from 'react';
import * as THREE from 'three';
import type { MaterialProps, ModelExtendedProps } from '../types';
import { ViewerLayer } from './ViewerLayer';
import { toThreeSide } from './Enums';
import { RENDER_ORDER_CUBOID } from './RenderOrder';
import { type GLTFLoader } from 'three-stdlib';
import { FileLoaderOptionsContext } from './FileLoaderOptionsContext';
import { useOnClick } from './useOnClick';
import { type ThreeEvent } from '@react-three/fiber';
import { TransformTargetGroup } from './TransformTargetGroup';

export const Model: FC<ModelExtendedProps> = ({
  id,
  url,
  position,
  rotation,
  scale,
  material: normalMaterialProps,
  isVisible,
  renderOrder = RENDER_ORDER_CUBOID,
  enableClippingByVisibilityBox = true,
  showInSphericalView = false,
  isClickable = true,
  onViewerObjectReady,
  addMaterial,
  removeMaterial,
  onClick,
}) => {
  const fileLoaderOptions = useContext(FileLoaderOptionsContext);

  const gltfData = useGLTF(url, undefined, undefined, (loader: GLTFLoader) => {
    loader.setWithCredentials(fileLoaderOptions.withCredentials);
  });

  useEffect(() => {
    return () => {
      useGLTF.clear(url);
    };
  }, [url]);

  const updateMaterial = useCallback(
    (material: THREE.Material, materialProps: MaterialProps | undefined) => {
      if (!materialProps) {
        return;
      }
      if (
        material instanceof THREE.MeshBasicMaterial ||
        material instanceof THREE.MeshPhongMaterial ||
        material instanceof THREE.MeshStandardMaterial ||
        material instanceof THREE.MeshLambertMaterial
      ) {
        const { color, transparent, opacity, side, emisiveColor, emisiveIntencity } = materialProps;
        if (color !== undefined) {
          material.color = new THREE.Color(color);
        }
        if (transparent !== undefined) {
          material.transparent = transparent;
        }
        if (opacity !== undefined) {
          material.opacity = opacity;
        }
        if (side !== undefined) {
          material.side = toThreeSide(side);
        }
        if (!(material instanceof THREE.MeshBasicMaterial)) {
          material.emissive = new THREE.Color(emisiveColor);
          material.emissiveIntensity = emisiveIntencity ?? 0.5;
        }
        material.needsUpdate = true;
      }
    },
    []
  );

  const getNewMaterial = useCallback(
    (material: THREE.Material, materialProps: MaterialProps | undefined) => {
      const needNonBasicMaterial = !!materialProps?.emisiveColor;
      if (needNonBasicMaterial && material instanceof THREE.MeshBasicMaterial) {
        return new THREE.MeshPhongMaterial({
          ...material,
          emissiveIntensity: materialProps.emisiveIntencity ?? 0.5,
          emissive: new THREE.Color(materialProps.emisiveColor),
          blendDstAlpha: material.blendDstAlpha ?? undefined,
          blendSrcAlpha: material.blendSrcAlpha ?? undefined,
          blendEquationAlpha: material.blendEquationAlpha ?? undefined,
          clippingPlanes: undefined,
          shadowSide: material.shadowSide ?? undefined,
        });
      }
      return material;
    },
    []
  );

  const enableClippingPlanes = enableClippingByVisibilityBox;

  const [allMeshes, setAllMeshes] = useState<Set<THREE.Mesh>>();

  const updateScene = useCallback(
    (scene: THREE.Group, materialProps: MaterialProps | undefined) => {
      const allMeshes0 = new Set<THREE.Mesh>();
      const needNonBasicMaterial = !!materialProps?.emisiveColor;
      scene.traverse((child) => {
        if (isClickable) {
          child.layers.enable(ViewerLayer.AutoPickable);
        }
        if (child.isObject3D) {
          const mesh = child as THREE.Mesh;
          if (mesh.isMesh) {
            allMeshes0.add(mesh);
            mesh.renderOrder = renderOrder;
            mesh.geometry.computeBoundingBox();
            if (Array.isArray(mesh.material)) {
              mesh.material = mesh.material.map((material) =>
                getNewMaterial(material, materialProps)
              );
              mesh.material.forEach((meshMaterial) => {
                updateMaterial(meshMaterial, materialProps);
                addMaterial(meshMaterial, enableClippingPlanes);
              });
            } else {
              if (needNonBasicMaterial && mesh.material instanceof THREE.MeshBasicMaterial) {
                mesh.material = getNewMaterial(mesh.material, materialProps);
              }
              updateMaterial(mesh.material, materialProps);
              addMaterial(mesh.material, enableClippingPlanes);
            }
          }
        }
      });
      setAllMeshes(allMeshes0);
    },
    [enableClippingPlanes, renderOrder, addMaterial, updateMaterial, getNewMaterial, isClickable]
  );

  const disposeScene = useCallback(
    (scene: THREE.Group) => {
      scene.traverse((child) => {
        if (child.isObject3D) {
          const mesh = child as THREE.Mesh;
          if (mesh.isMesh) {
            mesh.geometry.dispose();
            if (Array.isArray(mesh.material)) {
              mesh.material.forEach((meshMaterial) => {
                removeMaterial(meshMaterial);
                meshMaterial.dispose();
              });
            } else {
              removeMaterial(mesh.material);
              mesh.material.dispose();
            }
          }
        }
      });
    },
    [removeMaterial]
  );

  useEffect(() => {
    if (gltfData) {
      updateScene(gltfData.scene, normalMaterialProps);
    }
    return () => {
      if (gltfData) {
        disposeScene(gltfData.scene);
      }
    };
  }, [gltfData, normalMaterialProps, updateScene, disposeScene]);

  useEffect(() => {
    onViewerObjectReady(true);
    return () => {
      onViewerObjectReady(false);
    };
  }, [onViewerObjectReady]);

  const [modelGroup, setModelGroup] = useState<THREE.Group>();

  const handleModelGroup = useCallback((group: THREE.Group) => {
    setModelGroup(group ?? undefined);
  }, []);

  useEffect(() => {
    if (modelGroup) {
      modelGroup.traverse((child) => {
        const { layers } = child;
        if (isVisible ?? true) {
          layers.enable(ViewerLayer.Default);
          if (showInSphericalView) {
            layers.enable(ViewerLayer.SphericalViewerLayer);
          } else {
            layers.disable(ViewerLayer.SphericalViewerLayer);
          }
        } else {
          layers.disable(ViewerLayer.Default);
          layers.disable(ViewerLayer.SphericalViewerLayer);
        }
      });
    }
  }, [isVisible, modelGroup, showInSphericalView, isClickable]);

  const shouldHandle = useCallback((event: ThreeEvent<MouseEvent>) => {
    const { altKey, ctrlKey, metaKey, shiftKey, button } = event;
    const isLeftButton = button === 0;
    if (!isLeftButton || altKey || ctrlKey || metaKey || shiftKey) {
      return false;
    }
    return true;
  }, []);

  const onMeshClick = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      const intersection = event.intersections[0];
      const { object, point } = intersection;
      if (onClick && object instanceof THREE.Mesh && allMeshes?.has(object)) {
        // event.stopPropagation();
        const { x, y, z } = point;
        onClick([x, y, z], event);
      }
    },
    [onClick, allMeshes]
  );

  const { onPointerDown, onPointerUp } = useOnClick({ onClick: onMeshClick, shouldHandle });

  return (
    <TransformTargetGroup
      targetId={id ?? url}
      position={position}
      rotation={rotation}
      scale={scale}
    >
      <group ref={handleModelGroup}>
        <primitive
          object={gltfData.scene}
          onPointerUp={onPointerUp}
          onPointerDown={onPointerDown}
        />
      </group>
    </TransformTargetGroup>
  );
};
